import { FC } from 'react';
import { useFragment, graphql, RelayProp } from 'react-relay';
import {
    SharedRelatedItemsWrapper,
    ChildrenProps as GridProps,
} from '../SharedRelatedItemsWrapper/SharedRelatedItemsWrapper';
import { SharedItemTile } from '../../shared/SharedItemTile/SharedItemTile';
import { SharedMobileRelatedItem } from '../../shared/SharedMobileRelatedItem/SharedMobileRelatedItem';
import { recentSales } from '../../shared/sharedMessages';
import { RESULTS_FIRST_ROW_COUNT } from '../../constants/sbConstants';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy';
import { useUserSessionRegions } from 'dibs-regional-info/exports/regionalInfoHelpers';
import { useShowItemLocationBadge } from '../../shared/hooks/useShowItemLocationBadge';
import {
    trackItemsImpression,
    trackItemClick,
    Item as ItemTrackProps,
} from '../SharedRelatedItemsWrapper/helpers/sharedRelatedItemsWrapperTracking';

import { SharedRecentSales_viewer$key } from './__generated__/SharedRecentSales_viewer.graphql';
import { SharedRecentSales_itemSearch$key } from './__generated__/SharedRecentSales_itemSearch.graphql';
import { SharedRecentSales_user$key } from './__generated__/SharedRecentSales_user.graphql';

type Props = {
    viewer: SharedRecentSales_viewer$key;
    itemSearch: SharedRecentSales_itemSearch$key;
    user: SharedRecentSales_user$key;
    isMobile: boolean;
    isClient: boolean;
    relay: RelayProp;
};

export const SharedRecentSales: FC<Props> = ({
    viewer: viewerRef,
    itemSearch: itemSearchRef,
    user: userRef,
    isMobile,
    isClient,
    relay,
}) => {
    const viewer = useFragment(
        graphql`
            fragment SharedRecentSales_viewer on Viewer {
                ...SharedItemTile_viewer
            }
        `,
        viewerRef
    );

    const itemSearch = useFragment(
        graphql`
            fragment SharedRecentSales_itemSearch on ItemSearchQueryConnection
            @argumentDefinitions(
                userCountryCode: { type: "String", defaultValue: "" }
                priceBookName: { type: "String" }
            ) {
                pageType
                ...SharedItemTile_itemSearch
                soldItemResults: soldResults(first: 28) {
                    edges {
                        node {
                            item {
                                serviceId
                                ...SharedMobileRelatedItem_item
                                    @arguments(
                                        userCountryCode: $userCountryCode
                                        priceBookName: $priceBookName
                                    )
                                ...SharedItemTile_item
                                    @arguments(
                                        isTrade: $isTrade
                                        userCountryCode: $userCountryCode
                                        priceBookName: $priceBookName
                                        showSeller: $showSeller
                                    )
                                ...sharedRelatedItemsWrapperTracking_item
                            }
                        }
                    }
                }
            }
        `,
        itemSearchRef
    );

    const user = useFragment(
        graphql`
            fragment SharedRecentSales_user on User {
                ...SharedRelatedItemsWrapper_user
                ...SharedMobileRelatedItem_user
                ...SharedItemTile_user
            }
        `,
        userRef
    );

    const { soldItemResults, pageType } = itemSearch;
    const items = soldItemResults?.edges?.map(itemNode => itemNode?.node?.item);
    const itemIds = items?.map(item => item?.serviceId) || [];
    const userRegions = useUserSessionRegions() || [];
    const showItemLocationBadge = useShowItemLocationBadge();

    const onItemClick = ({ item, index }: { item: ItemTrackProps; index: number }): void => {
        trackItemClick({
            items: [item],
            startIndex: index,
            list: `${pageType} related sold items`,
            userRegions,
        });
    };

    if (!itemIds.length) {
        return null;
    }

    return (
        <SharedRelatedItemsWrapper
            user={user}
            header={recentSales}
            relay={relay}
            isMobile={isMobile}
            isClient={isClient}
            itemIds={itemIds}
            showSwiper={isMobile}
            initialCount={RESULTS_FIRST_ROW_COUNT}
            onItemsImpression={({ startIndex, itemsLengthToShow }) => {
                if (items?.length) {
                    trackItemsImpression({
                        items: [...items].splice(startIndex, itemsLengthToShow),
                        startIndex,
                        list: `${pageType} related sold items`,
                        userRegions,
                    });
                }
            }}
        >
            {({ itemClassName, ...itemProps }: GridProps) =>
                items?.filter(filterFalsy).map((item, index) => {
                    return isMobile ? (
                        <SharedMobileRelatedItem
                            key={item.serviceId}
                            index={index}
                            item={item}
                            user={user}
                            ymalHeartsEnabled
                            onClick={() => onItemClick({ item, index })}
                            {...itemProps}
                            {...itemClassName(index)}
                        />
                    ) : (
                        <SharedItemTile
                            key={item.serviceId}
                            index={index}
                            viewer={viewer}
                            item={item}
                            itemSearch={itemSearch}
                            user={user}
                            showCreatorName
                            showSellerName={false}
                            showItemLocationBadge={showItemLocationBadge}
                            isClient={isClient}
                            fetchFolderOnMouseOver
                            isMobile={false}
                            onClick={() => onItemClick({ item, index })}
                            {...itemProps}
                            {...itemClassName(index)}
                        />
                    );
                })
            }
        </SharedRelatedItemsWrapper>
    );
};
